import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { applyPageMeta } from '../lib/seo';
import { PAGE_META } from '../lib/routeMeta';
import { useAuth } from '../lib/auth/AuthContext';
import { apiJson, apiFetch, ApiError } from '../lib/api';
import { RegisterLayout } from '../components/register/RegisterLayout';
import { Toggle } from '../components/form/Toggle';
import { Button } from '../components/Button';
import { FormError } from '../components/form/FormError';

const PRIVACY_URL = 'https://www.statefarm.com/customer-care/privacy-security/privacy';

type PatchResponse = {
  status: number;
  data: {
    receivesEpisodeReminders: boolean;
    marketingConsent: boolean | null;
    changed: boolean;
  };
};

/**
 * /account — communication preferences + sign-out + delete-account.
 *
 * Auth-gated: anonymous users get bounced to /, PENDING users get bounced to
 * /register (their toggles aren't meaningful until registration completes).
 * Toggles are optimistic-update with revert-on-error; each PATCH lands an
 * audit row server-side (source='account-page'). Delete Account is stubbed
 * until the deletion flow ships in a later session.
 */
export default function AccountPage() {
  const auth = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    applyPageMeta(PAGE_META.account);
  }, []);

  // Route-gate. Wait for loading to resolve, then bounce non-auth / PENDING
  // users to the right place. Mirrors the pattern in RegisterPage.
  useEffect(() => {
    if (auth.status === 'loading') return;
    if (!auth.isAuthenticated) {
      navigate('/', { replace: true });
      return;
    }
    if (auth.user?.status === 'PENDING') {
      navigate('/register', { replace: true });
    }
  }, [auth.status, auth.isAuthenticated, auth.user, navigate]);

  // Mirror the auth context's user fields into local toggle state so the UI
  // can optimistically reflect a flip before the PATCH resolves.
  const [marketingConsent, setMarketingConsent] = useState<boolean>(
    auth.user?.marketingConsent === true,
  );
  const [reminders, setReminders] = useState<boolean>(
    auth.user?.receivesEpisodeReminders ?? true,
  );
  const [busy, setBusy] = useState<'marketing' | 'reminders' | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Keep local state in sync if the auth user updates from elsewhere (e.g.
  // a /auth/refresh fired by another tab via the AUTH_EVENTS.refreshed
  // listener in AuthContext).
  useEffect(() => {
    if (!auth.user) return;
    setMarketingConsent(auth.user.marketingConsent === true);
    setReminders(auth.user.receivesEpisodeReminders);
  }, [auth.user]);

  const onChangeMarketing = async (next: boolean): Promise<void> => {
    if (!auth.user) return;
    const previous = marketingConsent;
    setMarketingConsent(next);
    setBusy('marketing');
    setError(null);
    try {
      await apiJson<PatchResponse>('/me/email-preferences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ marketingConsent: next }),
      });
      auth.setUser({ ...auth.user, marketingConsent: next });
    } catch (err) {
      setMarketingConsent(previous);
      setError(err instanceof ApiError ? err.message : 'Could not save preference. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const onChangeReminders = async (next: boolean): Promise<void> => {
    if (!auth.user) return;
    const previous = reminders;
    setReminders(next);
    setBusy('reminders');
    setError(null);
    try {
      await apiJson<PatchResponse>('/me/email-preferences', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ receivesEpisodeReminders: next }),
      });
      auth.setUser({ ...auth.user, receivesEpisodeReminders: next });
    } catch (err) {
      setReminders(previous);
      setError(err instanceof ApiError ? err.message : 'Could not save preference. Please try again.');
    } finally {
      setBusy(null);
    }
  };

  const onLogout = async (): Promise<void> => {
    await auth.logout();
    navigate('/', { replace: true });
  };

  // Delete-account confirmation modal state. The destructive button opens
  // the modal; only an explicit confirm inside the modal fires the DELETE.
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const openDelete = (): void => {
    setDeleteError(null);
    setDeleteOpen(true);
  };

  const cancelDelete = (): void => {
    if (deleteBusy) return;
    setDeleteOpen(false);
    setDeleteError(null);
  };

  const confirmDelete = async (): Promise<void> => {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const res = await apiFetch('/me/account', { method: 'DELETE' });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new ApiError(res.status, body?.message ?? `Request failed (${res.status})`, body);
      }
      // Account is now disabled. Clear the local session and bounce home.
      // logout() also revokes server-side, but the DELETE handler already
      // revoked the refresh-token row so this is best-effort cleanup.
      await auth.logout();
      navigate('/', { replace: true });
    } catch (err) {
      setDeleteBusy(false);
      setDeleteError(
        err instanceof ApiError
          ? err.message
          : 'Could not process the deletion request. Please try again or email support@statefarmgamerhood.com.',
      );
    }
  };

  // Hold the layout shell during auth resolution so the redirect effect can
  // fire before we render the toggles bound to a (possibly stale) user.
  if (auth.status === 'loading' || !auth.user || auth.user.status === 'PENDING') {
    return <RegisterLayout>{null}</RegisterLayout>;
  }

  return (
    <RegisterLayout
      heading={
        <>
          <img
            src="/assets/gamerhood-wordmark.png"
            alt="Gamerhood"
            className="block h-auto w-[175px] md:w-[292px]"
          />
          <span className="sr-only">Account settings</span>
        </>
      }
    >
      <div className="flex flex-col gap-[40px] text-white">
        <BackLink onClick={() => navigate(-1)} />

        <div className="flex flex-col gap-[40px] md:px-[20px]">
          <Toggle
            checked={marketingConsent}
            onChange={onChangeMarketing}
            disabled={busy === 'marketing'}
            label={
              <span className="block">
                I understand and consent to State Farm using my personal information that I
                provide in my Contest Registration for business, marketing, and commercial
                purposes consistent with State Farm Privacy Policy accessible at{' '}
                <a
                  href={PRIVACY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline underline-offset-2 hover:text-[#00ffb2]"
                >
                  {PRIVACY_URL.replace(/^https?:\/\//, '')}
                </a>
                . Use for marketing may include direct and indirect communications from State
                Farm and/or a third party acting on behalf of State Farm.
              </span>
            }
          />

          <Toggle
            checked={reminders}
            onChange={onChangeReminders}
            disabled={busy === 'reminders'}
            label="Receive Weekly Episode Reminder Emails"
          />

          {error && <FormError>{error}</FormError>}

          {/* Desktop: row, 200px buttons, space-between across the full row.
              Mobile: column, full-width buttons stacked. Extra mt-[10px]
              opens additional breathing room above the destructive action. */}
          <div className="mt-[10px] flex flex-col gap-[16px] md:flex-row md:items-center md:justify-between">
            <Button variant="dark" onClick={onLogout} className="w-full md:w-[200px]">
              Log Out
            </Button>
            <Button variant="destructive" onClick={openDelete} className="w-full md:w-[200px]">
              Delete Account
            </Button>
          </div>
        </div>
      </div>

      {deleteOpen && (
        <DeleteAccountModal
          busy={deleteBusy}
          error={deleteError}
          onConfirm={confirmDelete}
          onCancel={cancelDelete}
        />
      )}
    </RegisterLayout>
  );
}

type DeleteAccountModalProps = {
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
};

/**
 * Confirmation dialog for the Delete Account action. Renders a backdrop
 * overlay (click + Escape both cancel), explains the consequences in plain
 * language, and disables both buttons while the DELETE request is in flight.
 */
function DeleteAccountModal({ busy, error, onConfirm, onCancel }: DeleteAccountModalProps) {
  const titleId = useId();
  const descId = useId();
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Move focus into the dialog on open so keyboard users can immediately
  // act on it. Esc closes (mirrors the AppBar menus' pattern).
  useEffect(() => {
    confirmRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);

  return (
    // Backdrop click is a pointer-only supplement to the Escape handler
    // above — keyboard users already have an equivalent close path.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel();
      }}
    >
      <div className="w-full max-w-[480px] rounded-lg border border-white/15 bg-[#2a2237] p-6 text-white shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
        <h2 id={titleId} className="font-['Orbitron',sans-serif] text-[20px] uppercase tracking-[0.04em]">
          Delete your account?
        </h2>
        <p id={descId} className="mt-4 text-[14px] leading-[1.55] text-white/85">
          This will deactivate your Gamerhood account immediately and sign you out on every
          device. Your data is held until we manually complete the removal — until then you{' '}
          <strong className="text-white">won&rsquo;t be able to re-register with this email
          address.</strong>
        </p>
        <p className="mt-3 text-[13px] leading-[1.55] text-white/65">
          Need it removed sooner, or changed your mind after submitting? Email{' '}
          <a
            href="mailto:support@statefarmgamerhood.com"
            className="underline underline-offset-2 hover:text-[#00ffb2]"
          >
            support@statefarmgamerhood.com
          </a>
          .
        </p>

        {error && (
          <div className="mt-4">
            <FormError>{error}</FormError>
          </div>
        )}

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-end">
          <Button variant="dark" onClick={onCancel} disabled={busy} className="sm:min-w-[140px]">
            Cancel
          </Button>
          <Button
            ref={confirmRef}
            variant="destructive"
            onClick={onConfirm}
            disabled={busy}
            className="sm:min-w-[200px]"
          >
            {busy ? 'Deleting…' : 'Yes, Delete My Account'}
          </Button>
        </div>
      </div>
    </div>
  );
}

type BackLinkProps = { onClick: () => void };

/**
 * Figma-spec back link (node 362:1155). Differs from the registration
 * BackLink (which is gated by step state) — this one is a pure history
 * back. Kept inline because it's not reused elsewhere yet.
 */
function BackLink({ onClick }: BackLinkProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group inline-flex items-center gap-[10px] self-start font-['Arial',sans-serif] text-[18px] text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#413850]"
    >
      <span className="flex h-[30px] w-[30px] items-center justify-center rounded-full border border-white/80 transition-colors group-hover:border-[#00ffb2] group-hover:text-[#00ffb2]">
        <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 12H5" />
          <path d="M12 19l-7-7 7-7" />
        </svg>
      </span>
      <span className="transition-colors group-hover:text-[#00ffb2]">Back</span>
    </button>
  );
}
